import { useState, useCallback, useRef } from 'react';
import { Delete, NotePencil, Copy, ArrowUp } from '@sweep-io/sweep-design/dist/icons';
import { ActionsMenu, ActionsMenuAction } from '../../../common/actions-menu/ActionsMenu';
import ConfirmDestructiveDialog from '../../../common/dialogs/ConfirmDestructiveDialog';
import { PromptWithCategory } from '../types';
import useAgentsChats from '../../useAgentsChats';
import { useCopyToClipboard } from '../copy-to-clipboard/useCopyToClipboard';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { telemetry } from '../../../../telemetry';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';

type PromptAction = 'edit' | 'delete' | 'copy' | 'use';

interface PromptRowActionsMenuProps {
  prompt: PromptWithCategory;
  onEdit: (prompt: PromptWithCategory) => void;
  onDelete: ({ groupId, promptId }: { groupId: string; promptId: string }) => void;
  onUseInChatSuccess: () => void;
}

export const PromptRowActionsMenu = ({
  prompt,
  onEdit,
  onDelete,
  onUseInChatSuccess,
}: PromptRowActionsMenuProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { onConfirmMessage, activeAgent } = useAgentsChats();
  const containerRef = useRef<HTMLDivElement>(null);
  const { handleCopy } = useCopyToClipboard();
  const { addNotification } = useSweepNotifications();

  const handleMenuOpenChange = useCallback((isOpen: boolean) => {
    const row = containerRef.current?.closest('.SweepDataTableRow');
    if (row) {
      row.setAttribute('data-menu-open', String(isOpen));
    }
  }, []);

  const handleUseInChat = useCallback(async () => {
    if (!activeAgent) return;

    try {
      await onConfirmMessage({
        messageStr: prompt.promptText,
        agentId: activeAgent.id,
        agentType: activeAgent.type,
        isFirstMessage: true,
      });
      onUseInChatSuccess();
    } catch (error) {
      telemetry.captureError(new Error('Failed to use prompt in chat'), error);
      addNotification({
        message: 'Failed to use prompt in chat',
        variant: SweepNotificationVariant.Error,
      });
    }
  }, [activeAgent, onConfirmMessage, prompt.promptText, onUseInChatSuccess, addNotification]);

  const handleConfirmDelete = () => {
    onDelete?.({ groupId: prompt.categoryId, promptId: prompt.id });
    setIsDeleteDialogOpen(false);
  };

  const actions: ActionsMenuAction<PromptAction>[] = [
    {
      value: 'edit',
      label: 'Edit Prompt',
      actionIcon: <NotePencil size="medium" />,
    },
    {
      value: 'delete',
      label: 'Delete Prompt',
      actionIcon: <Delete size="medium" />,
      addBottomDivider: true,
    },
    {
      value: 'copy',
      label: 'Copy to clipboard',
      actionIcon: <Copy size="medium" />,
    },
    {
      value: 'use',
      label: 'Use this Prompt in Chat',
      actionIcon: <ArrowUp size="medium" />,
      disabled: !activeAgent,
    },
  ];

  const handleActionClick = useCallback(
    async (action: ActionsMenuAction<PromptAction>) => {
      switch (action.value) {
        case 'edit':
          onEdit(prompt);
          break;
        case 'delete':
          setIsDeleteDialogOpen(true);
          break;
        case 'copy':
          await handleCopy(prompt.promptText);
          break;
        case 'use':
          await handleUseInChat();
          break;
      }
    },
    [handleCopy, handleUseInChat, onEdit, prompt],
  );

  return (
    <div ref={containerRef}>
      <ActionsMenu
        actions={actions}
        onClick={handleActionClick}
        iconTooltip="Actions"
        menuWidth="228px"
        onOpenChange={handleMenuOpenChange}
      />
      <ConfirmDestructiveDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Prompt"
        confirmText="Delete"
        cancelText="Cancel"
      >
        Are you sure you want to delete this prompt? This action cannot be undone.
      </ConfirmDestructiveDialog>
    </div>
  );
};
